import { z } from "zod";

import type { PrimitiveValue, PrimitiveValueName, ValueNameToType } from "@skilitics/nautilus-core";
import { PrimitiveValueName as primitiveValueName } from "@skilitics/nautilus-core/schemas";

import { ValueSubtype } from "./valueSubtype.ts";

const primitiveValue = z.union([z.boolean(), z.number(), z.string()]) satisfies z.ZodType<PrimitiveValue>;

/** A value that is right here, rather than a pointer to one over there. */
export const isPrimitiveValue = (candidate: unknown): candidate is PrimitiveValue =>
  typeof candidate === "boolean" || typeof candidate === "number" || typeof candidate === "string";

/**
 * The stored keys are abbreviated because a configured value is written once per
 * value slot in every design: `id` and `p` are the producing node's id and the
 * name it advertises the value under, `e` an external value's property name,
 * `l` the lock marking the reference as its slot's type authority. These keys
 * never leave this module — `valueReference` and the constructors below are the
 * only way in and out, and they speak in full names.
 */
const storedNodeValue = z.object({
  id: z.number().int(),
  p: z.string().min(1),
  l: z.literal(true).optional(),
});

const storedExternalValue = z.object({
  e: z.string().min(1),
});

/**
 * The branch of a value config that lives somewhere else, under the reserved
 * `__vc` key — the design-time counterpart of the runtime `__dv` reference, and
 * reserved for the same reason: a value config sits among arbitrary config keys,
 * so it has to be recognisable by shape alone, without the node's manifest. That
 * recognisability is what lets the framework track, repoint and reset the
 * reference, so a node needs no `nodeReferences` entry for a value it points at.
 *
 * The other branch — the value right here — is the bare literal, so only a
 * reference costs anything. Designer-side only: publish emits a raw literal, a
 * judy resolvable, or a `__dv` reference depending on the consuming seam; a
 * ReferencedValue never ships.
 */
export const ReferencedValue = z
  .object({
    __vc: z.union([storedNodeValue, storedExternalValue]),
  })
  .strict();
export type ReferencedValue = z.infer<typeof ReferencedValue>;

/**
 * A reference to a value a node designer advertises via `getValues`.
 *
 * A locked reference is the type authority for its slot — an expression subject,
 * whose type and subtype the rest of the expression derives from. When its node
 * is removed it is tombstoned to a {@link ValueTombstone} rather than cleared,
 * so the slot keeps its signature.
 */
export interface NodeValueReference {
  nodeId: number;
  name: string;
  locked?: true;
}

/** A reference to an external (environment) value. */
export interface ExternalValueReference {
  property: string;
}

/** What a ReferencedValue points at, unwrapped from its marker key and stored keys. */
export type ValueReference = NodeValueReference | ExternalValueReference;

/** What a ReferencedValue points at, named as the rest of the designer names it. */
export const valueReference = ({ __vc }: ReferencedValue): ValueReference =>
  "id" in __vc ? { nodeId: __vc.id, name: __vc.p, ...(__vc.l && { locked: __vc.l }) } : { property: __vc.e };

/**
 * The stored halves of a tombstone: `t` the base type, `s` the full serialized
 * subtype instance, options included — together the signature of the value the
 * removed node advertised. Like `__vc`'s keys, these never leave this module:
 * `valueSignature` and `toValueTombstone` speak in full names.
 */
const storedValueTombstone = z.object({
  t: primitiveValueName,
  s: ValueSubtype.optional(),
});

/**
 * The Value Tombstone branch of a value config, under the reserved `__tv` key:
 * typed, but no value. Left where a locked reference pointed at a removed node,
 * so the slot keeps the signature of what it was bound to — the repair criteria
 * for re-binding — while the value itself resolves to `undefined`.
 */
export const ValueTombstone = z
  .object({
    __tv: storedValueTombstone,
  })
  .strict();
export type ValueTombstone = z.infer<typeof ValueTombstone>;

/** The type-and-subtype a tombstone preserves: what the removed value looked like. */
export interface ValueSignature {
  type: PrimitiveValueName;
  subtype?: ValueSubtype;
}

/** A tombstone's signature, named as the rest of the designer names it. */
export const valueSignature = ({ __tv }: ValueTombstone): ValueSignature => ({
  type: __tv.t,
  ...(__tv.s && { subtype: __tv.s }),
});

/** A value config carrying `signature` but no value — the only way a ValueTombstone is built. */
export const toValueTombstone = (signature: ValueSignature): ValueTombstone => ({
  __tv: { t: signature.type, ...(signature.subtype && { s: signature.subtype }) },
});

/**
 * How a value is configured: the value right here as a raw literal, a
 * {@link ReferencedValue} pointing at one over there, or a {@link ValueTombstone}
 * where a locked reference's node has gone. All branches are the config — that a
 * literal needs no wrapper is a storage economy, not a distinction the reader of
 * a value slot has to care about.
 *
 * The one shape every value-taking config field declares, so a slot never
 * spells the union out itself.
 */
export const ValueConfig = z.union([primitiveValue, ReferencedValue, ValueTombstone]);
export type ValueConfig = z.infer<typeof ValueConfig>;

/**
 * A value config whose literal branch is narrowed to a base type. No tombstone
 * branch: a typed slot like this is authored directly, never locked as a type
 * authority, so removal clears it rather than tombstoning it.
 */
export type ValueConfigFor<TName extends PrimitiveValueName> = ValueNameToType<TName> | ReferencedValue;

/**
 * The reserved `values` key on a node's design-time config: a {@link ValueConfig}
 * per value slot. This mirrors a variable being either a bare literal or a
 * resolve-object (hence `values`, not `resolvableValues`), and is why pre-existing
 * literal values need no migration.
 *
 * An entry may also be unset. A value slot is offered before it is filled, and
 * `resetNodeValueRefs` clears one whose node has gone, so `undefined` is a state
 * the design reaches on its own — rejecting it here would make the config
 * carrying it unsaveable rather than merely unconfigured.
 */
export const ValueConfigs = z.record(z.string(), ValueConfig.optional());
export type ValueConfigs = z.infer<typeof ValueConfigs>;

/** Which branch of a value config this is: a reference over there, or a literal right here. */
export const isReferencedValue = (candidate: unknown): candidate is ReferencedValue =>
  typeof candidate === "object" && candidate !== null && "__vc" in candidate;

/** Which branch of a value config this is: a tombstone — typed, but no value. */
export const isValueTombstone = (candidate: unknown): candidate is ValueTombstone =>
  typeof candidate === "object" && candidate !== null && "__tv" in candidate;

/** Which of the two things a ReferencedValue points at. */
export const isNodeValueReference = (reference: ValueReference): reference is NodeValueReference =>
  "nodeId" in reference;

/**
 * The node value a value config points at, or undefined when it is a literal or
 * points somewhere else. The framework's hook for tracking node references
 * without consulting the node's manifest. The reference read back is a copy, so
 * repointing one means storing a fresh {@link toReferencedValue}.
 */
export const referencedNodeValue = (candidate: unknown): NodeValueReference | undefined => {
  if (!isReferencedValue(candidate)) return undefined;
  const reference = valueReference(candidate);
  return isNodeValueReference(reference) ? reference : undefined;
};

/** A value config pointing at `reference` — the only way a ReferencedValue is built. */
export const toReferencedValue = (reference: ValueReference): ReferencedValue =>
  isNodeValueReference(reference)
    ? { __vc: { id: reference.nodeId, p: reference.name, ...(reference.locked && { l: reference.locked }) } }
    : { __vc: { e: reference.property } };
